import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, Loader2, AlertCircle } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const returnTo = searchParams.get('from') || location.state?.from || '/dashboard';

  useSEO({
    title: 'Create Account | PlanningSearch',
    description: 'Create your free PlanningSearch account. Browse live planning applications, set up alerts, and get AI-powered project insights for builders, architects and tradespeople.',
    canonical: 'https://planningsearch.com/signup',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(email, password, name);
      navigate(returnTo);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-56px)] flex" data-testid="signup-page">
      {/* Left: Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="font-['Manrope'] text-2xl font-extrabold tracking-tight text-zinc-900">Create your account</h1>
            <p className="text-sm text-zinc-500 mt-1">Start finding building projects today</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md mb-5" data-testid="signup-error">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="John Smith"
                  className="w-full pl-10 pr-4 py-2.5 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-zinc-900 placeholder:text-zinc-400"
                  data-testid="signup-name-input" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-zinc-900 placeholder:text-zinc-400"
                  data-testid="signup-email-input" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Min. 6 characters"
                  className="w-full pl-10 pr-4 py-2.5 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-zinc-900 placeholder:text-zinc-400"
                  data-testid="signup-password-input" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-emerald-700 text-white font-semibold rounded-md py-2.5 text-sm hover:bg-emerald-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="signup-submit-button">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account...</> : 'Create account'}
            </button>
          </form>

          <p className="text-center text-sm text-zinc-500 mt-6">
            Already have an account?{' '}
            <Link to={`/login${returnTo !== '/dashboard' ? `?from=${encodeURIComponent(returnTo)}` : ''}`} className="text-emerald-600 hover:text-emerald-700 font-medium" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>

      {/* Right: Image */}
      <div className="hidden lg:block lg:w-[45%] relative">
        <img src="https://images.unsplash.com/photo-1710883734889-5a0b8ab6bfcf?w=900&q=60" alt="Modern architecture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-emerald-900/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-xl font-['Manrope'] font-bold leading-snug">"The AI reports save us hours of manual research every week."</p>
          <p className="text-sm text-white/70 mt-2">— Sarah A., Architect, Peckham</p>
        </div>
      </div>
    </div>
  );
}
